import React, { memo, useCallback, useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, Redirect } from 'react-router-dom';
import format from 'date-fns/format';
import PATHS from 'routes/paths';

import {
  selectOrderById,
  acceptOrder,
  rejectOrder,
  completeOrder,
  sendReceipt,
  shipOrder,
  getOrder,
} from 'redux/slices/order';

import DashboardContainer from 'components/DashboardContainer';
import Image from 'components/Image';
import Button from 'components/Button';
import { orderDetail as TEXT } from 'locales/en';
import { priceFromAPI } from 'utils/price';
import {
  PENDING_PAYMENT,
  PENDING,
  ACCEPTED,
  REJECTED,
  SHIPPED,
  COMPLETED,
} from 'utils/transform';

const OrderDetail = () => {
  let { id } = useParams();
  const orderDetail = useSelector((state) => selectOrderById(state, id)) || {};
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(getOrder({ id }));
  }, [dispatch, id]);

  const {
    shopper_email,
    shopper_name,
    shopper_surname,
    shopper_phone_number,
    orderProducts,
    created_at,
    currency_symbol,
    status,
    tracking_id,
    total,
  } = orderDetail;
  const [trackingId, setTrackingId] = useState(tracking_id);

  const shouldRequestDelivery = useMemo(() => {
    const result =
      orderProducts?.map((item) => item.product.data.should_request_delivery) ||
      [];

    return result.some((item) => !!item);
  }, [orderProducts]);

  const SHIP = useMemo(
    () => ({
      title: TEXT.shipOrder,
      action: shipOrder({ id, tracking_id: trackingId }),
    }),
    [id, trackingId]
  );
  const SEND_RECEIPT = useMemo(
    () => ({
      title: TEXT.sendReceipt,
      action: sendReceipt(id),
    }),
    [id]
  );
  const ACCEPT = useMemo(
    () => ({ title: TEXT.acceptOrder, action: acceptOrder(id) }),
    [id]
  );
  const REJECT = useMemo(
    () => ({ title: TEXT.rejectOrder, action: rejectOrder(id) }),
    [id]
  );
  const COMPLETE = useMemo(
    () => ({
      title: TEXT.complete,
      action: completeOrder(id),
    }),
    [id]
  );

  const renderButtons = useCallback(() => {
    let buttons = [];
    if (status === PENDING) {
      buttons = [ACCEPT, REJECT];
    } else if (status === ACCEPTED && shouldRequestDelivery) {
      buttons = [SHIP, SEND_RECEIPT];
    } else if (status === SHIPPED) {
      buttons = [COMPLETE, SEND_RECEIPT];
    } else if ([ACCEPTED, COMPLETED].indexOf(status) > -1) {
      buttons = [SEND_RECEIPT];
    } else if ([PENDING_PAYMENT, REJECTED].indexOf(status) > -1) {
      buttons = [];
    }
    return (
      !!buttons.length && (
        <>
          {!!buttons[1] && (
            <button
              data-testid="cta-1"
              className="border-0 underline text-javelin-blueGray lg:text-base lg:font-light focus:outline-none"
              onClick={() => dispatch(buttons[1].action)}
            >
              {buttons[1].title}
            </button>
          )}
          <Button
            title={buttons[0].title}
            className="btn-primary py-4 px-8 w-auto ml-4 lg:ml-14"
            onClick={() => dispatch(buttons[0].action)}
            testId="cta-2"
          />
        </>
      )
    );
  }, [
    ACCEPT,
    COMPLETE,
    REJECT,
    SEND_RECEIPT,
    SHIP,
    dispatch,
    shouldRequestDelivery,
    status,
  ]);

  if (!orderDetail.id) return <Redirect to={PATHS.orders} />;

  return (
    <DashboardContainer header={TEXT.header1} subheader={TEXT.header2}>
      <div className="card h-full p-7">
        <div className="grid grid-cols-2 items-center">
          <p className="text-xs lg:text-sm text-opacity-60 text-javelin-gray-dark">
            {format(new Date(created_at), 'dd MMMM yyyy')}
          </p>
          <p className="green-label ml-auto">{status}</p>
        </div>
        <hr className="horizontal-line" />
        <div id="basic-info" className="lg:relative h-auto lg:mt-8">
          {!!orderProducts.length && (
            <>
              {orderProducts.map((item, idx) => (
                <div
                  key={`${item.product_id}-${idx}`}
                  className="grid grid-cols-2 items-center mb-2"
                >
                  <div className="lg:flex items-center mr-auto hidden">
                    <div className="hidden lg:w-20 lg:h-20 lg:block overflow-hidden rounded-md">
                      <Image
                        src={item.product.data.image}
                        alt={item.product.data.name}
                      />
                    </div>
                    <p className="text-xs lg:text-sm text-javelin-gray-light ml-5">
                      {item.product?.data?.name || '-'}
                    </p>
                  </div>
                  <div className="lg:flex items-center justify-between w-48 ml-auto text-javelin-gray-light text-xs lg:text-sm hidden">
                    <p>
                      {currency_symbol}
                      {priceFromAPI(item.total) || '-'}
                    </p>
                    <p>x {item.quantity || 1}</p>
                    <p className="text-right">
                      {currency_symbol}
                      {priceFromAPI(item.total)}
                    </p>
                  </div>
                  <p className="lg:hidden text-xs lg:text-sm text-javelin-gray-light">
                    {item.quantity || 1} x {item.product?.data?.name || '-'}
                  </p>
                  <p className="lg:hidden text-xs lg:text-sm text-javelin-gray-dark text-right text-opacity-60">
                    {currency_symbol}
                    {priceFromAPI(item.total)}
                  </p>
                </div>
              ))}
              <hr className="horizontal-line" />
            </>
          )}
          <p className="ml-auto text-right font-bold text-javelin-gray-dark text-base">
            {currency_symbol}
            {total}
          </p>
        </div>
      </div>

      <div
        id="buyer-info"
        className="card lg:min-h-60 p-7 lg:p-8 lg:relative h-auto mt-8 lg:grid lg:grid-cols-2"
      >
        <div className="grid grid-cols-2 items-start gap-4 lg:px5">
          <p className="text-xs lg:text-sm text-javelin-blueGray">
            {TEXT.buyer}
          </p>
          <div className="text-xs lg:text-sm text-javelin-gray-dark">
            <p className="py-0.5">
              {shopper_name || '-'} {shopper_surname || '-'}
            </p>
            <p className="py-0.5">{shopper_email}</p>
            <p className="py-0.5">{shopper_phone_number}</p>
          </div>
        </div>
        <hr className="horizontal-line lg:hidden" />
        <div className="lg:border-l lg:horizontal-line lg:pl-5">
          <div className="grid grid-cols-2 items-center gap-4 lg:px-5">
            <p className="text-xs lg:text-sm text-javelin-blueGray">
              {TEXT.trackingNo}
            </p>
            <input
              type="text"
              className="input h-8 lg:h-12 rounded text-sm text-javelin-gray-dark"
              defaultValue={tracking_id}
              onChange={(e) => setTrackingId(e.target.value)}
              disabled={
                !!tracking_id || !(status === ACCEPTED && shouldRequestDelivery)
              }
            />
          </div>
        </div>
      </div>
      <div className="flex self-end items-center mt-4 lg:mt-7">
        {renderButtons()}
      </div>
    </DashboardContainer>
  );
};

export default memo(OrderDetail);
